import { cloneElement, isValidElement, useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import {
  describedBy, errorKeys, fieldIds, hasIssues, issuesAfterBlur, issuesAfterChange, serverErrorView, validateAll,
  type FieldErrors, type FieldIssues, type FormSchema,
} from './fieldModel';

export type { FieldErrors, FormSchema, FieldRule } from './fieldModel';
export { firstErrorKey, hasErrors, errorKeys, rules } from './fieldModel';

/** Props `Field` puts on its control (spread them when using the render-prop form). */
export interface FieldControlProps {
  id: string;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
  'aria-required'?: true;
  required?: boolean;
}

export interface FieldProps {
  /** Visible label (never a placeholder). */
  label: ReactNode;
  /** Control id; generated when omitted. Pass the field name to make `focusField(name)` work. */
  id?: string;
  /** One line under the control, in text-2. */
  hint?: ReactNode;
  /** The error for this field (validated on blur and on submit, never while typing). */
  error?: ReactNode;
  /** Required convention: `*` (aria-hidden) + `required` on the control. */
  required?: boolean;
  /** Optional convention: "(אופציונלי)" after the label. Use one convention per form. */
  optional?: boolean;
  /**
   * The control. A single element gets id / aria-* / required injected (and `input-error`
   * appended to its className on error); a function receives those props to spread itself.
   */
  children: ReactElement | ((control: FieldControlProps) => ReactNode);
  /** Visually hide the label (it stays for screen readers). For inline toolbars only. */
  hideLabel?: boolean;
  className?: string;
}

/**
 * Label + control + hint + error, wired for assistive tech (PRD §4.17, DS §13).
 * Styling stays on the existing `.input` / `.label` / `.error-text` classes.
 */
export default function Field({ label, id, hint, error, required, optional, children, hideLabel, className }: FieldProps) {
  const { t } = useTranslation();
  const autoId = useId();
  const controlId = id ?? `f${autoId.replace(/:/g, '')}`;
  const ids = fieldIds(controlId);
  const hasError = Boolean(error);

  const control: FieldControlProps = {
    id: controlId,
    ...(hasError ? { 'aria-invalid': true as const } : {}),
    'aria-describedby': describedBy(controlId, { hint: Boolean(hint), error: hasError }),
    ...(required ? { required: true, 'aria-required': true as const } : {}),
  };

  let rendered: ReactNode;
  if (typeof children === 'function') {
    rendered = children(control);
  } else if (isValidElement<{ className?: string; 'aria-describedby'?: string }>(children)) {
    const own = children.props;
    const extra = own['aria-describedby'];
    rendered = cloneElement(children, {
      ...control,
      'aria-describedby': describedBy(controlId, { hint: Boolean(hint), error: hasError, extra }),
      className: [own.className, hasError ? 'input-error' : ''].filter(Boolean).join(' ') || undefined,
    } as Partial<typeof own>);
  } else {
    rendered = children;
  }

  return (
    <div className={className}>
      <label htmlFor={controlId} className={hideLabel ? 'sr-only' : 'label'}>
        {label}
        {required && <span aria-hidden="true" className="text-danger-700 ms-0.5">*</span>}
        {optional && !required && <span className="ms-1 font-normal text-surface-600">{t('form.optional')}</span>}
      </label>
      {rendered}
      {hint && !hasError && (
        <p id={ids.hint} className="text-xs text-surface-600 mt-1">{hint}</p>
      )}
      {hint && hasError && (
        // keep the hint id resolvable while the error is shown
        <p id={ids.hint} className="sr-only">{hint}</p>
      )}
      {hasError && (
        <p id={ids.error} className="error-text">
          <AlertCircle aria-hidden="true" className="w-3.5 h-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

/**
 * "יש 2 שדות לתיקון" after a failed submit (role="alert"), each item a link
 * that moves focus to its field. Render it at the top of the form only after
 * a submit attempt; pass the field labels so the list reads naturally.
 */
export function FormErrorSummary({
  errors, labels, order, className,
}: {
  errors: FieldErrors;
  /** Field name → visible label. Fields without a label are counted but not listed. */
  labels?: Record<string, ReactNode>;
  /** Visual order of the fields. */
  order?: readonly string[];
  className?: string;
}) {
  const { t } = useTranslation();
  const keys = errorKeys(errors, order);
  if (keys.length === 0) return null;
  return (
    <div role="alert" className={`rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700 ${className ?? ''}`}>
      <p className="font-medium">{t('form.errorSummary', { count: keys.length })}</p>
      {labels && (
        <ul className="mt-1 list-disc ps-5">
          {keys.filter((k) => labels[k]).map((k) => (
            <li key={k}>
              <a
                href={`#${k}`}
                className="underline underline-offset-2"
                onClick={(e) => { e.preventDefault(); focusField(k); }}
              >
                {labels[k]}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Focus a field by its control id (scrolls it into view). Returns false when not found. */
export function focusField(id: string): boolean {
  const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
  if (!el) return false;
  el.focus({ preventScroll: true });
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  return true;
}

/** After a failed submit: focus the first invalid control inside `root` (DOM order). */
export function focusFirstInvalid(root: ParentNode | null | undefined): boolean {
  const el = root?.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (!el) return false;
  el.focus({ preventScroll: true });
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  return true;
}

export { fieldIds };

/**
 * The form pattern of DS §13 in one hook: validate on blur and on submit,
 * never while typing (typing only clears an error that is already shown); on
 * a failed submit show `FormErrorSummary` and focus the first invalid field.
 *
 * The schema is keyed by the control ids (so the summary links and
 * `focusField` find them); `values` holds the same ids.
 *
 *   const v = useFormValidation(schema, { 'ct-title': title });
 *   <form noValidate onSubmit={v.submit(() => save())}>
 *     {v.showSummary && <FormErrorSummary errors={v.errors} labels={…} order={…} />}
 *     <Field id="ct-title" label=… required error={v.errors['ct-title']}>
 *       <input className="input" value={title} onChange={…} onBlur={() => v.blur('ct-title')} />
 *     </Field>
 */
export function useFormValidation(schema: FormSchema, values: Record<string, unknown>) {
  const { t } = useTranslation();
  const [issues, setIssues] = useState<FieldIssues>({});
  const [submitted, setSubmitted] = useState(false);

  // Clear errors the user has fixed (never add one while typing).
  const signature = Object.keys(schema).map((k) => String(values[k] ?? '')).join('\u0000');
  useEffect(() => {
    setIssues((prev) => issuesAfterChange(prev, schema, values));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the field values only
  }, [signature]);

  const blur = useCallback((id: string) => {
    setIssues((prev) => issuesAfterBlur(prev, schema, id, values));
  }, [schema, values]);

  const errors: FieldErrors = useMemo(() => {
    const out: FieldErrors = {};
    for (const [id, issue] of Object.entries(issues)) if (issue) out[id] = t(issue.key, issue.params);
    return out;
  }, [issues, t]);

  /** Wrap the submit handler: runs `onValid` only when every field passes. */
  const submit = (onValid: () => void) => (e?: FormEvent) => {
    e?.preventDefault();
    const all = validateAll(schema, values);
    setIssues(all);
    if (hasIssues(all)) {
      setSubmitted(true);
      const first = errorKeys(all as FieldErrors, Object.keys(schema))[0];
      // After the error state renders (aria-invalid, summary), move focus.
      requestAnimationFrame(() => { if (first) focusField(first); });
      return;
    }
    setSubmitted(false);
    onValid();
  };

  const reset = useCallback(() => { setIssues({}); setSubmitted(false); }, []);

  return {
    errors,
    blur,
    submit,
    reset,
    /** A submit failed and errors remain: show `FormErrorSummary`. */
    showSummary: submitted && hasIssues(issues),
  };
}

/**
 * A rejected save, in Hebrew (PRD §4.17, voice §7.4): what happened and what
 * to do, never a code in the main line. The server's own Hebrew sentence is
 * shown as it is; anything else gets a line by its kind (`fallback` for an
 * unknown failure), and the raw text sits under "פרטים טכניים".
 */
export function FormServerError({ error, fallback, className = '' }: { error: unknown; fallback: string; className?: string }) {
  const { t } = useTranslation();
  const boxRef = useRef<HTMLDivElement>(null);
  // P-56 (D-231): the error sits at the top of a long, scrolled form — bring it into view,
  // so a failed save is never a form that "just does nothing".
  useEffect(() => {
    if (error) boxRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [error]);
  if (!error) return null;
  const view = serverErrorView(error);
  // "What happened" (the caller's fallback) + "what to do" (by kind), unless
  // the server already said it in Hebrew.
  const stop = /[.!?…]$/.test(fallback.trim()) ? fallback.trim() : `${fallback.trim()}.`;
  const main = view.kind === 'message' ? view.message : `${stop} ${t(`form.server.${view.kind}`)}`;
  return (
    <div ref={boxRef} role="alert" className={`rounded-lg border border-danger-200 bg-danger-50 px-3 py-2.5 text-sm text-danger-700 ${className}`}>
      <p className="flex items-start gap-2">
        <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <span className="min-w-0"><bdi>{main}</bdi></span>
      </p>
      {view.detail && (
        <details className="mt-1 ps-6 text-xs text-text-2">
          <summary className="cursor-pointer rounded-xs hover:text-text">{t('states.details')}</summary>
          <p className="mt-1 break-words"><bdi dir="ltr">{view.detail}</bdi></p>
        </details>
      )}
    </div>
  );
}
